import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, XCircle, Truck, Package, User, MapPin, FileText, Calendar, History, Undo2 } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { PageHeader } from '../../components/shared/PageHeader';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../components/ui/alert-dialog';

export default function AdminOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    adminService.getOrderById(id)
      .then(data => { setOrder(data); setLoading(false); })
      .catch(() => navigate('/admin/porudžbine'));
  }, [id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (action, label) => {
    setActionLoading(true);
    try {
      let updated;
      if (action === 'reject') updated = await adminService.rejectOrder(id);
      else if (action === 'ship') updated = await adminService.shipOrder(id);
      else if (action === 'deliver') updated = await adminService.deliverOrder(id);
      else if (action === 'notPickedUp') updated = await adminService.notPickedUpOrder(id);
      setOrder(updated);
      toast.success(`Porudžbina uspešno: ${label}`);
    } catch (e) {
      toast.error(e?.message || 'Greška pri ažuriranju');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-60" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!order) return null;

  const SectionCard = ({ title, icon: Icon, children }) => (
    <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-border bg-muted/30">
        <Icon size={17} className="text-primary" />
        <h3 className="font-serif text-base font-semibold text-foreground">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={order.orderNumber}
        subtitle={`Kreirano: ${new Date(order.createdAt).toLocaleString('sr-RS')}`}
        backTo="/admin/porudžbine"
        actions={<StatusBadge status={order.status} deliveryType={order.deliveryType} size="lg" />}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left - main info */}
        <div className="xl:col-span-2 space-y-5">
          {/* Customer */}
          <SectionCard title="Podaci o kupcu" icon={User}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm font-ui">
              <div><span className="text-muted-foreground">Ime i prezime:</span><span className="text-foreground ml-2 font-medium">{order.firstName} {order.lastName}</span></div>
              <div><span className="text-muted-foreground">Telefon:</span><span className="text-foreground ml-2 font-medium">{order.phone}</span></div>
              <div className="sm:col-span-2"><span className="text-muted-foreground">Email:</span><span className="text-foreground ml-2">{order.email}</span></div>
            </div>
          </SectionCard>

          {/* Address */}
          <SectionCard title="Adresa dostave" icon={MapPin}>
            <div className="text-sm font-ui">
              <p className="text-foreground font-medium">{order.address}</p>
              <p className="text-muted-foreground">{order.city}, {order.postalCode}</p>
              {order.note && (
                <div className="mt-3 p-3 bg-accent/60 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Napomena:</p>
                  <p className="text-foreground">{order.note}</p>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Order items */}
          <SectionCard title="Stavke porudžbine" icon={Package}>
            <div className="space-y-3">
              {order.items.map((item, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 py-2.5 border-b border-border last:border-0">
                  <div>
                    <p className="font-ui text-sm font-medium text-foreground">{item.name}</p>
                    <p className="text-xs text-muted-foreground font-ui">{item.quantity} x {item.price.toLocaleString('sr-RS')} RSD / {item.unit}</p>
                  </div>
                  <span className="font-ui font-bold text-foreground">{item.total.toLocaleString('sr-RS')} RSD</span>
                </div>
              ))}
              <Separator className="my-3" />
              <div className="space-y-1.5 text-sm font-ui">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{order.subtotal.toLocaleString('sr-RS')} RSD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dostava</span>
                  <span>{order.shipping === 0 ? 'BESPLATNO' : `${order.shipping} RSD`}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-base">
                  <span>Ukupno</span>
                  <span className="text-primary">{order.total.toLocaleString('sr-RS')} RSD</span>
                </div>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Right - sidebar */}
        <div className="space-y-5">
          {/* Actions */}
          <div className="bg-card rounded-xl shadow-card border border-border p-5">
            <h3 className="font-serif text-base font-semibold text-foreground mb-4">Akcije</h3>

            {order.status === 'NEW' && (
              <div className="space-y-2">
                <Button
                  onClick={() => handleAction('ship', order.deliveryType === 'PICKUP' ? 'označena kao spakovana' : 'označena kao poslata')}
                  disabled={actionLoading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui"
                >
                  {actionLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Truck size={16} className="mr-2" />}
                  {order.deliveryType === 'PICKUP' ? 'Oznaci kao spakovanu' : 'Oznaci kao poslatu'}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground font-ui">
                      <XCircle size={16} className="mr-2" />
                      Odbij porudzbinu
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-serif">Odbiti porudzbinu?</AlertDialogTitle>
                      <AlertDialogDescription className="font-ui">
                        Da li ste sigurni da želite odbiti porudzbinu {order.orderNumber}? Ova akcija je nepovrativa.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="font-ui">Otkazati</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleAction('reject', 'odbijena')}
                        className="bg-destructive text-destructive-foreground font-ui"
                      >
                        Odbij
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {order.status === 'SHIPPED' && (
              <div className="space-y-2">
                <Button
                  onClick={() => handleAction('deliver', 'označena kao preuzeta')}
                  disabled={actionLoading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary-light font-ui"
                >
                  {actionLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Package size={16} className="mr-2" />}
                  Oznaci kao preuzetu
                </Button>
                <Button
                  onClick={() => handleAction('notPickedUp', 'označena kao nije preuzeta')}
                  disabled={actionLoading}
                  variant="outline"
                  className="w-full border-orange-400 text-orange-700 hover:bg-orange-50 font-ui"
                >
                  {actionLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Undo2 size={16} className="mr-2" />}
                  Oznaci kao nije preuzeta
                </Button>
              </div>
            )}

            {(order.status === 'DELIVERED' || order.status === 'REJECTED' || order.status === 'NOT_PICKED_UP') && (
              <div className="text-center py-4">
                <StatusBadge status={order.status} deliveryType={order.deliveryType} size="lg" />
                <p className="text-xs text-muted-foreground font-ui mt-2">
                  {order.status === 'DELIVERED'
                    ? 'Porudžbina je zavrsena'
                    : order.status === 'NOT_PICKED_UP'
                      ? 'Porudžbina nije preuzeta'
                      : 'Porudžbina je odbijena'}
                </p>
              </div>
            )}
          </div>

          {/* Customer history */}
          <div className="bg-card rounded-xl shadow-card border border-border p-5">
            <h3 className="font-serif text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <History size={17} className="text-primary" />
              Istorija kupca
            </h3>
            <div className="space-y-3 text-sm font-ui">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tip kupca</span>
                {order.isReturningCustomer ? (
                  <span className="badge-gold">Stalnicupac</span>
                ) : (
                  <span className="badge-green">Nov kupac</span>
                )}
              </div>
              {order.isReturningCustomer && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prethodne narudžbine</span>
                    <span className="text-foreground font-medium">{order.previousOrdersCount}x</span>
                  </div>
                  {order.lastOrderDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Poslednja narudžbina</span>
                      <span className="text-foreground">{new Date(order.lastOrderDate).toLocaleDateString('sr-RS')}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
